define([
	"csi/_base/underscore",
	"csi/config",
	"csi/def",
	"csi/def/Action",
	"csi/html",
	"csi/locale",
	"csi/xml"
], function(_,config,def,Action,html,locale,xml) {
	
	var CSS_SECTION_ICON = "csi-specification-section-icon";

	var TYPE_SDI = "SpecificationDataSheetItem";
	var TYPE_SSI = "SpecificationSectionItem";
	var TYPE_SID = "SpecificationItemDefinition";

	var _sharedAction = new Action();
	_sharedAction.m_class = CSS_SECTION_ICON + " csi-icon-link";
	_sharedAction.setMaterialIcon("link");
	
	var _editAction = new Action();
	_editAction.m_class = CSS_SECTION_ICON + " csi-icon-link" + " csi-icon-edit-link";
	_editAction.setMaterialIcon("link");

	var _unsharedAction = new Action();
	_unsharedAction.m_class = CSS_SECTION_ICON + " csi-icon-link-off";
	_unsharedAction.m_typeControl = def.ACTION_ICON;

	/**
	 * Return shared section url referenced by section if shared, in edit mode, or unlinked
	 * @param {Context} ctx
	 * @param {Node} [section]
	 * @return {string}
	 */
	function findRelatedSectionUrl(ctx, section) {
		section = section || ctx.getNode();

		if (isSectionShared(ctx, section)) {
			return section.$URL;
		} else if (isSectionUnlinked(ctx, section)) {
			return section.Original; 
		} else if (isSectionEdited(ctx, section)) {
			var baseline = ctx.cache.getNode(section.Baseline);
			if (baseline) {
				return baseline.Original; 
			}
		}
		
		return null;
	}
	
	/**
	 * Return shared section node for section if section is shared, in edit mode, or unlinked
	 * @param {Context} ctx
	 * @param {Node} [dataSheetRevision]
	 * @return {string[]}
	 */
	function getDataSheetSharedSectionUrls(ctx, dataSheetRevision) {
		dataSheetRevision = dataSheetRevision || ctx.getNode();

		var sections = ctx.cache.getNodes(dataSheetRevision.AllSections);
		
		return _.reduce(sections, function(memo, section) {
			var url = findRelatedSectionUrl(ctx, section);
			if (url) {
				memo.push(url);
			}
			
			return memo;
		}, []);
	}
	
	/**
	 * Return true if given attribute is part of the SpecificationItemDefinition attribute map
	 * @param {string} id - attribute id
	 * @param {Node} node - either SpecificationSectionItem or SpecificationItemDefinition
	 * @param {Cache} cache
	 * @return {boolean}
	 */
	function isAttributeInSectionItemAttributeMap(id, node, cache) {
		var typeNode = node.$Type;
		var bSection = (typeNode == TYPE_SSI);
		var bDefinition = (typeNode == TYPE_SID);
		var sid;

		if (bSection) {
			sid = cache.getNode(node.Definition);
		} else if (bDefinition) {
			sid = node;
		}

		var mapping = sid ? config.getNodeSetting(sid, "SectionItemAttributeMap") : null;
		if (mapping) {
			var attrs = bSection ? _.values(mapping) : _.keys(mapping);
			if (_.contains(attrs, id)) {
				return true;
			}
		}

		return false;
	}

	/** 
	 * Return true if SpecificationDataSheetItem is a new item in an edited shared section
	 * @param {Context} ctx 
	 * @param {Object} [sdi]
	 * @return {boolean}
	 */
	function isDataSheetItemNewInEditedSection(ctx, sdi) {
		sdi = sdi || ctx.getNode();
		var section = ctx.cache.getNode(sdi.Section);
		var ssi = ctx.cache.getNode(sdi.Actual);
		console.assert(!!section);
		console.assert(!!ssi);
		return isSectionEdited(ctx, section) && !xml.isValidURL(ssi.Original);
	}
	
	/** 
	 * Return true if SpecificationItemDefinition is special
	 * @param {Context} ctx 
	 * @param {Object} [sid]
	 * @return {boolean}
	 */
	function isDefinitionAdHoc(ctx, sid) {
		sid = sid || ctx.getNode();
		return sid.Special;
	}

	/**
	 * Return true if SID's property is different from baseline SID's property
	 * @param {Context} ctx 
	 * @param {Object} sdi
	 * @param {Object} key
	 * @return {boolean}
	 */
	function isDifferentFromBaselineDefinition(ctx, sdi, key) {
		var cache = ctx.cache;
		var ssi = cache.getNode(sdi.Actual);
		var def = cache.getNode(ssi.Definition);
		var originalSSI = ssi && cache.getNode(ssi.Original);
		var originalDef = originalSSI && cache.getNode(originalSSI.Definition);
		var currentValue = def[key];
		var originalValue = originalDef[key];
		return !xml.isSameValue(currentValue, originalValue);
	}
	
	/**
	 * Return true if SpecificationSection is ad-hoc (section definition is special)
	 * @param {Context} ctx
	 * @param {Node} [section] - if not specified, then assumes section is in current context
	 * @return {boolean}
	 */
	function isSectionAdHoc(ctx, section) {
		section = section || ctx.getNode();
		var definition = section && ctx.cache.getNode(section.Definition);
		console.assert(!!definition);
		return definition.Special;
	}
	
	/**
	 * Return true if SpecificationSection is shared (not special)
	 * @param {Context} ctx
	 * @param {Node} [section] - if not specified, then assumes section is in current context
	 * @return {boolean}
	 */
	function isSectionEdited(ctx, section) {
		section = section || ctx.getNode();
		console.assert(!!section);
		return xml.isValidURL(section.Baseline);
	}
	
	/**
	 * Return true if SpecificationSection is shared (not special)
	 * @param {Context} ctx
	 * @param {Node} [section] - if not specified, then assumes section is in current context
	 * @return {boolean}
	 */
	function isSectionShared(ctx, section) {
		section = section || ctx.getNode();
		console.assert(!!section);
		return !section.Special;
	}

	/**
	 * Return true if SpecificationSection is unlinked from a shared section
	 * @param {Context} ctx
	 * @param {Node} [section] - if not specified, then assumes section is in current context
	 * @return {boolean}
	 */
	function isSectionUnlinked(ctx, section) {
		section = section || ctx.getNode();
		console.assert(!!section);
		return xml.isValidURL(section.Original) && !xml.isValidURL(section.Baseline);
	}

	/**
	 * Return true if SpecificationSectionItem attributes have different values from their SpecificationItemDefinition
	 * counterparts in the attribute mapping 
	 * @param {Context} ctx
	 * @param {Node} [ssi] - if not specified, then assumes SpecificationSectionItem is in current context
	 * @return {boolean}
	 */
	function isSectionItemChangedFromMapping(ctx, ssi) {
		ssi = ssi || ctx.getNode();
		var sid = ctx.cache.getNode(ssi.Definition);
		var mapping = config.getNodeSetting(sid, "SectionItemAttributeMap");

		var b = _.some(mapping, function(ssiId, sidId) {
			var type = xml.nodeAttributeGetType(sid, sidId);
			var v1 = sid[sidId];
			var v2 = ssi[ssiId];
			return !_.isEqual(v1, v2) && xml.valueIsValid(v1, type);
		});

		return b;
	}

	/**
	 * Render section definition prefix if linked or unlinked section
	 * @param {Context} ctx
	 * @param {Node} [section] - if not specified, then assumes section is in current context
	 * @param {DOMNode} domNode - content will be added as child of domNode
	 * @return {DOMNode}
	 */
	function renderSectionDefinition(ctx, section, domNode) {
		section = section || ctx.getNode();
		console.assert(!!section);
		
		var definition = ctx.cache.getNode(section.Definition);
		console.assert(!!definition);

		var name = locale.getNodeName(definition, ctx.cache, ctx.locale);
		var bLinked = isSectionShared(ctx, section) || isSectionUnlinked(ctx, section) || isSectionEdited(ctx, section);

		return html.create("span", {
			"class": "bold",
			"innerText": name + (bLinked ? ": " : "")
		}, domNode);
	}

	/**
	 * Render icon for section based on whether it is linked or unlinked
	 * @param {Context} ctx
	 * @param {Node} [section] - if not specified, then assumes section is in current context
	 * @param {DOMNode} domNode - content will be added as child of domNode
	 * @return {DOMNode}
	 */
	function renderSectionIcon(ctx, section, domNode) {
		var action = isSectionEdited(ctx, section) ? _editAction : (isSectionShared(ctx, section) ? _sharedAction : _unsharedAction);
		return action.toHtmlControl(ctx, domNode);
	}
	
	return {
		CSS_SECTION_ICON: CSS_SECTION_ICON,
		
		TYPE_SDI: TYPE_SDI,
		TYPE_SSI: TYPE_SSI,
		TYPE_SID: TYPE_SID,
		
		getDataSheetSharedSectionUrls: getDataSheetSharedSectionUrls,
		isAttributeInSectionItemAttributeMap: isAttributeInSectionItemAttributeMap,
		isDataSheetItemNewInEditedSection: isDataSheetItemNewInEditedSection,
		isDefinitionAdHoc: isDefinitionAdHoc,
		isDifferentFromBaselineDefinition: isDifferentFromBaselineDefinition,
		isSectionAdHoc: isSectionAdHoc,
		isSectionEdited: isSectionEdited,
		isSectionShared: isSectionShared,
		isSectionUnlinked: isSectionUnlinked,
		isSectionItemChangedFromMapping: isSectionItemChangedFromMapping,
		renderSectionDefinition: renderSectionDefinition,
		renderSectionIcon: renderSectionIcon
	};
});